import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, Subject } from 'rxjs';
import { environment } from 'src/environments/environment';

interface Theme {
  [key: string]: string;
}

interface ThemesResponse {
  colors: { [themeName: string]: Theme; };
}

@Injectable({
  providedIn: 'root',
})
export class ThemeService {
  private themeClass = 'dark';
  private themeChangedSubject = new Subject<void>();
  public themeChanged$ = this.themeChangedSubject.asObservable();
  private themeParamMap: { [key: string]: string; } = {
    'wf': 'WARFIGHTERS',
    'warfighters': 'WARFIGHTERS',
    'pavcon': 'PavCon',
    'safire': 'SAFiRE',
    'rubi': 'RUBi',
    'dimnd': 'DiMND',
    'emirld': 'EMiRLD'
  };

  constructor(private http: HttpClient) {
    this.loadInitialTheme();
  }

  toggleTheme(): void {
    this.isDarkTheme() ? this.activateLightMode() : this.activateDarkMode();
  }

  activateDarkMode(): void {
    this.setDarkTheme(true);
  }

  activateLightMode(): void {
    this.setDarkTheme(false);
  }

  private setDarkTheme(isDark: boolean): void {
    document.body.classList.toggle(this.themeClass, isDark);
    localStorage.setItem('theme', JSON.stringify(isDark));
  }

  loadInitialTheme(): void {
    const storedTheme = localStorage.getItem('theme');
    const isDark = storedTheme ? JSON.parse(storedTheme) : false;
    this.setDarkTheme(isDark);
  }

  getThemeNameFromParam(themeParam: string | null): string {

    if (!themeParam) {
      const currentTheme = this.getCurrentThemeName() || environment.defaultTheme;

      return currentTheme;
    }

    const themeName = this.themeParamMap[themeParam.toLowerCase()];

    return themeName || environment.defaultTheme;
  }

  applyThemeByName(themeName: string): void {

    this.getThemes().subscribe((themesResponse) => {
      const themes = themesResponse.colors;
      const theme = themes[themeName] ?? themes[environment.defaultTheme];
      if (theme) {

        localStorage.setItem(`${environment.projectName}.theme`, themeName);
        this.themeChangedSubject.next();
        this.applyTheme(theme);
      } else {
        console.warn('Theme not found:', themeName);
      }
    });
  }

  private applyTheme(theme: Theme): void {
    Object.entries(theme).forEach(([property, value]) => {
      document.documentElement.style.setProperty(`--${property}`, value);
    });
  }

  getThemes(): Observable<ThemesResponse> {
    return this.http.get<ThemesResponse>('/assets/template/colors.json');
  }

  getCurrentThemeName(): string {
    return (
      localStorage.getItem(`${environment.projectName}.theme`) ||
      environment.defaultTheme
    );
  }

  isDarkTheme(): boolean {
    return document.body.classList.contains(this.themeClass);
  }
}
